/**
 * Platform detection utilities:
 * - OS detection for platform-specific UI and deep links
 * - URL-based platform name extraction for event external links
 */

export type DetectedOS = 'ios' | 'android' | 'windows' | 'macos' | 'unknown';

/**
 * Detects user's operating system from user agent
 */
export function detectOS(): DetectedOS {
  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes('iphone') || ua.includes('ipad')) {
    return 'ios';
  }
  if (ua.includes('android')) {
    return 'android';
  }
  if (ua.includes('win')) {
    return 'windows';
  }
  if (ua.includes('mac')) {
    return 'macos';
  }

  return 'unknown';
}

/**
 * Gets platform-specific settings URL for date/time configuration.
 * On Android, returns a deep link that opens settings directly.
 * On iOS and desktop, returns null (users must open Settings manually).
 */
export function getTimeSettingsUrl(): string | null {
  const os = detectOS();

  switch (os) {
    case 'ios':
      // iOS deep links to settings are deprecated and non-functional on iOS 10+
      // Instruct user to open Settings manually
      return null;
    case 'android':
      // Android settings intent (works on most Android versions)
      return 'intent://settings/date_settings#Intent;scheme=android-app;end';
    default:
      // Desktop browsers can't deep link to settings
      return null;
  }
}

// --- URL-based platform name detection ---

const KNOWN_PLATFORMS: Record<string, string> = {
  'eventbrite.com': 'Eventbrite',
  'meetup.com': 'Meetup',
  // Meetup's own short domain, used in shared links.
  'meetu.ps': 'Meetup',
  // Luma serves both hosts interchangeably; the backend's Luma handling treats lu.ma and
  // luma.com (and their www. forms) as the same platform, so both must map here.
  'lu.ma': 'Luma',
  'luma.com': 'Luma',
  'facebook.com': 'Facebook',
  'linkedin.com': 'LinkedIn',
  'zoom.us': 'Zoom',
  'teams.microsoft.com': 'Microsoft Teams',
  'meet.google.com': 'Google Meet',
  'discord.gg': 'Discord',
  'discord.com': 'Discord',
  'youtube.com': 'YouTube',
  'twitch.tv': 'Twitch',
};

/**
 * Approximates a public suffix: a two-letter ccTLD or one of the common gTLDs, each optionally
 * carrying a two-letter ccTLD under it (.co.uk, .com.au).
 *
 * Deliberately not `[a-z]{2,}(\.[a-z]{2,})?`. Locations are user-supplied, and a tail that accepts
 * any two trailing labels lets eventbrite.evil.com wear the "Eventbrite" label, hiding the real
 * domain from everyone who reads the label instead of the URL. An unmatched host falls through to
 * the hostname fallback below, which shows the domain.
 */
const BRAND_TLD = '(?:[a-z]{2}|com|org|net)(?:\\.[a-z]{2})?';

const brandHostPattern = (label: string): RegExp => new RegExp(`(^|\\.)${label}\\.${BRAND_TLD}$`);

/**
 * Platforms that serve the same brand from many country TLDs. An exact-host map cannot express
 * "Eventbrite, on any TLD" and would need one hand-edit per country — production already holds
 * eventbrite.fr, .pt, .co.uk and .ca alongside .com, so the brand label is matched instead.
 *
 * Keyed on the registrable label (plain letters, spliced into the pattern verbatim), matched as
 * `<label>.<public suffix>` with optional subdomains. Compiled once at module load rather than
 * per call: getPlatformName runs on every event card render.
 */
const MULTI_TLD_PLATFORMS: readonly { readonly pattern: RegExp; readonly name: string }[] = [
  { pattern: brandHostPattern('eventbrite'), name: 'Eventbrite' },
  { pattern: brandHostPattern('meetup'), name: 'Meetup' },
];

/**
 * The hostname as this module labels it. Keeps the un-stripped hostname when stripping empties it:
 * a degenerate host of "www." strips to '', and an empty label would render as no location at all
 * on surfaces that gate on the label.
 */
function displayHost(hostname: string): string {
  return hostname.replace(/^www\./, '') || hostname;
}

/** Platform name for an already-normalized host, so each export parses the URL exactly once. */
function platformNameForHost(host: string): string {
  // Check exact match first. Typed check, not a bare truthiness index: a hostname like
  // "__proto__" or "constructor" otherwise resolves to an inherited Object.prototype member
  // and returns a non-string, which React refuses to render as a child.
  const exact: unknown = KNOWN_PLATFORMS[host];
  if (typeof exact === 'string') return exact;

  // Check if host ends with a known platform domain (handles subdomains like events.lu.ma)
  for (const [domain, name] of Object.entries(KNOWN_PLATFORMS)) {
    if (host.endsWith(`.${domain}`)) return name;
  }

  // Same brand on a country TLD (eventbrite.fr, eventbrite.co.uk, events.eventbrite.pt).
  for (const { pattern, name } of MULTI_TLD_PLATFORMS) {
    if (pattern.test(host)) return name;
  }

  // Fallback: capitalize first letter of domain
  return host.charAt(0).toUpperCase() + host.slice(1);
}

/**
 * Extract a human-readable platform name from a URL.
 * Checks against known event/social platforms, falls back to capitalized domain.
 */
export function getPlatformName(url: string): string {
  try {
    return platformNameForHost(displayHost(new URL(url).hostname));
  } catch {
    return 'External site';
  }
}

/**
 * Platform name with the host disclosed, as in "Eventbrite (eventbrite.fr)", for the accessible
 * name of a link whose URL is only visible on screen. A brand label on its own tells a
 * screen-reader user nothing about where a user-supplied link actually goes.
 *
 * The parenthetical is dropped when the name already is the host, so an unrecognised link reads
 * "Foo.com" rather than "Foo.com (foo.com)".
 */
export function getPlatformNameWithHost(url: string): string {
  try {
    const host = displayHost(new URL(url).hostname);
    const name = platformNameForHost(host);
    return name.toLowerCase() === host.toLowerCase() ? name : `${name} (${host})`;
  } catch {
    return 'External site';
  }
}
